import type { AxiosError } from 'axios';
import type { TFunction, ResourceKey } from 'i18next';
import type { Path, FieldValues, UseFormReturn } from 'react-hook-form';

import axios from 'axios';

const errorCodeTranslationKeyMapping: { [key: string]: ResourceKey } = {
  EMPLOYEE_NUMBER_ALREADY_EXISTS: 'errors.validation.employeeNumber.exists',
  EMAIL_ALREADY_EXISTS: 'errors.validation.email.exists',
  SSO_FOREIGN_ID_ALREADY_EXISTS: 'errors.validation.ssoForeignId.exists',
  USER_ALREADY_INVITED: 'errors.validation.username.alreadyInvited',
  USERNAME_ALREADY_EXISTS: 'errors.validation.username.exists',
  USERNAME_DOES_NOT_EXIST: 'errors.validation.username.notFound',
};

export function getErrorMessageTranslationKeyFromErrorCode(errorCode: string): ResourceKey {
  return errorCodeTranslationKeyMapping[errorCode];
}

export interface APIError {
  fieldErrors?: Array<APIFieldError>;
  message?: string;

  [key: string]: unknown;
}

export interface APIFieldError {
  field: string;
  message: string;
  objectName?: string;
}

export function isApiError(error: unknown): error is AxiosError<APIError> {
  return axios.isAxiosError<APIError>(error);
}

export function isServerError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { response?: { status?: number } };
  return err.response?.status !== undefined && err.response.status >= 500;
}

export function hasStatus(error: unknown, status: number): boolean {
  return isApiError(error) && error.response?.status === status;
}

export function isInsufficientDataError(error: unknown): boolean {
  // TODO: refactor to not rely on status code but on a distinct error string
  return hasStatus(error, 422);
}

export function getApiErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.response?.data?.message || error.message || 'There was an API error.';
  }

  if (error instanceof Error) {
    return error.message || error.name || 'An error occurred';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = error.message;
    if (typeof message === 'string') {
      return message;
    }
  }

  return 'Unknown error';
}

export function handleAPIFieldErrors<T extends FieldValues>(
  error: unknown,
  methods: UseFormReturn<T>,
  t: TFunction<'administration'>
): boolean {
  const apiError = error as APIError;
  if (
    !apiError?.fieldErrors ||
    !Array.isArray(apiError.fieldErrors) ||
    apiError.fieldErrors.length === 0
  ) {
    return false;
  }

  apiError.fieldErrors.forEach((fieldError: APIFieldError) => {
    const translatedMessage = t(getErrorMessageTranslationKeyFromErrorCode(fieldError.message));

    methods.setError(fieldError.field as Path<T>, {
      type: 'manual',
      message: translatedMessage,
    });
  });

  return true;
}
